<template>
  <div v-if="headModel" ref="diamondButtonRef">
    <div class="head-wrapper">
      <HeadFromModel />
    </div>
  </div>
  <div class="diamond-head-wrapper" v-else-if="remote" v-html="html" ref="diamondButtonRef"></div>
  <div v-else ref="diamondButtonRef">
    <div
      class="diamond-head"
      :class="{
        'diamond-head-solo': kind === 'solo' || kind === 'teamsolo',
        'diamond-head-team-effectiveness':
          kind === 'TeamEffectivenessAggregateDiamond' ||
          kind === 'TeamEffectivenessIndividualDiamond',
        'no-click-diamond-head': !clickable,
      }"
    >
      <div class="diamond-head-background"></div>
      <div class="diamond areaDiamond">
        <div class="diamondCrosshairHorizontal"></div>
        <div class="diamondCrosshairVertical"></div>

        <div v-if="altRatings || ratings">
          <Tooltip
            tag="div"
            v-for="(leaderType, index) in leaderTypes"
            :key="index"
            :text="tooltip(leaderType)"
          >
            <a
              :style="style(leaderType)"
              class="diamondButton leaderTypeTabButton diamondButton-circular"
              :class="css(leaderType)"
              @click="click(leaderType)"
              :href="clickableHref"
              :aria-label="`Leader type: ${leaderType}`"
            >
              <div class="diamond-button-hit-target"></div>
            </a>
          </Tooltip>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { TimelineMax, Back } from "gsap";
import { Tooltip } from "uiv";
import useDiamondHeadModel from "../composables/useDiamondHeadModel";
import {
  HeadApiModel,
  TeamEffectivenessRatingsApiModel,
  TeamEffectivenessReviewRatingsApiModel,
} from "../api-models";
import cms from "../services/cms/cms";
import cmsContentMap from "../services/cms/cms-content-map";
import { CmsTeamEffectivenessReview } from "../services/cms/cms-type-definitions";
import HeadFromModel from "./head/head-from-model.vue";
import { getTeamEffectivenessReport } from "./team-effectiveness/get-team-effectiveness-report-team-id";

type Ratings = TeamEffectivenessReviewRatingsApiModel | TeamEffectivenessRatingsApiModel | null;

const props = withDefaults(
  defineProps<{
    headId: string | null;
    kind:
      | "solo"
      | "viewpoints"
      | "teamsolo"
      | "teamviewpoints"
      | "TeamEffectivenessAggregateDiamond"
      | "TeamEffectivenessIndividualDiamond";
    ratings: Ratings;
    clickable: boolean;
    tooltips: boolean;
    animateEntrance: boolean;
  }>(),
  {
    headId: null,
    kind: "TeamEffectivenessAggregateDiamond",
    ratings: null,
    clickable: true,
    tooltips: true,
    animateEntrance: false,
  },
);

const emit = defineEmits(["leader-type-selection", "onDataLoaded"]);

const diamondButtonRef = ref<HTMLElement | null>(null);

const {
  getSoloHeadModel,
  getViewpointsHeadModel,
  getTeamSoloHeadModel,
  getTeamViewpointsHeadModel,
  getTeamEffectivenessIndividualHeadModel,
} = useDiamondHeadModel();

const remote = computed(
  () =>
    !["TeamEffectivenessAggregateDiamond", "TeamEffectivenessIndividualDiamond"].includes(
      props.kind,
    ),
);

const headModel = ref<HeadApiModel | null>(null);

const clickableHref = computed(() => {
  // eslint-disable-next-line no-script-url
  return props.clickable ? "javascript:void(0)" : "";
});

const altRatings = ref<Ratings>(null);

const leaderTypes = [
  "Professional",
  "Entrepreneurial",
  "Executional",
  "Practical",
  "Strategist",
  "Servant",
  "Transformational",
  "Charismatic",
  "Innovator",
];

function click(leaderType: string): void {
  if (props.clickable) {
    emit("leader-type-selection", leaderType);
  }
}

function css(leaderType: string): any[] {
  return [
    `btn-${leaderType.toLowerCase()} leaderType-${leaderType.toLowerCase()}`,
    {
      "team-diamond-head-shape": props.kind === "TeamEffectivenessAggregateDiamond",
    },
  ];
}

function style(leaderType: string): Record<string, any> {
  if (!(altRatings.value || props.ratings)) {
    return {
      display: "none",
    };
  }
  const axisScale = 18;
  const spotScale = 0.3;
  const positions = {
    Innovator: { x: 0, y: -2 },
    Entrepreneurial: { x: -1, y: -1 },
    Executional: { x: -2, y: -0 },
    Practical: { x: -1, y: 1 },
    Strategist: { x: 0, y: 2 },
    Servant: { x: 1, y: 1 },
    Transformational: { x: 2, y: 0 },
    Charismatic: { x: 1, y: -1 },
    Professional: { x: 0, y: 0 },
  };
  const x = positions[leaderType].x * axisScale;
  const y = positions[leaderType].y * axisScale;
  const sizePercent = (altRatings.value || props.ratings!).fits[leaderType] * spotScale;
  return {
    left: `${x - sizePercent * 0.5 + 50}%`,
    top: `${y - sizePercent * 0.5 + 50}%`,
    width: `${sizePercent}%`,
    height: `${sizePercent}%`,
    zIndex: 2,
    position: "absolute",
    opacity: 0.9,
  };
}

const doc = ref<CmsTeamEffectivenessReview | null>(null);

function tooltip(leaderType: string): string {
  if (!props.tooltips) {
    return ""; // Empty tooltips are not displayed at all;
  }
  const x: any = altRatings.value || props.ratings!;
  const y = x.ratings || x.meanRatings;
  const z = y[leaderType];
  const text = cms.asText(
    doc.value?.data.body.find((slice) => slice.primary.question_id === leaderType)?.primary
      .question_title,
  );
  return `${text}: ${z.toFixed(1)}`;
}

function getDotsByDescendingSize(dots: HTMLElement[]): HTMLElement[] {
  return dots.slice().sort((a, b) => {
    if (!a.style.width) {
      return -1;
    }
    if (!a.style.width) {
      return 1;
    }
    if (a.style.width < b.style.width) {
      return 1;
    }
    if (a.style.width > b.style.width) {
      return -1;
    }
    return 0;
  });
}

function runEntranceAnimation(): void {
  const dots: HTMLElement[] = getDotsByDescendingSize(
    Array.prototype.slice.call(diamondButtonRef.value?.getElementsByClassName("diamondButton")),
  );

  const timeline = new TimelineMax();
  for (let i = 0; i < dots.length; i++) {
    timeline.from(dots[i], 0.3, { height: 0, width: 0, delay: i * 0.2, ease: Back.easeOut }, 0);
  }
}

function emitDataLoaded(hasData: boolean): void {
  setTimeout(() => {
    emit("onDataLoaded", hasData);
    if (props.animateEntrance) {
      runEntranceAnimation();
    }
  }, 0);
}

function updateHtml(newKind: string): void {
  if (remote.value) {
    let modelPromise: Promise<HeadApiModel | null>;
    switch (props.kind) {
      case "solo":
        modelPromise = getSoloHeadModel();
        break;
      case "viewpoints":
        modelPromise = getViewpointsHeadModel();
        break;
      case "teamsolo":
        modelPromise = getTeamSoloHeadModel(props.headId || undefined);
        break;
      case "teamviewpoints":
        modelPromise = getTeamViewpointsHeadModel(props.headId || undefined);
        break;
      default:
        throw new Error(`Unexpected remote head kind: ${props.kind}`);
    }
    modelPromise.then((model) => {
      if (props.kind === newKind) {
        if (model) {
          model.clickable = props.clickable;
          model.showTooltips = props.tooltips;
          model.animateEntrance = props.animateEntrance;
        }
        headModel.value = model;
        emitDataLoaded(!!model);
      }
    });
  } else if (props.kind === "TeamEffectivenessIndividualDiamond") {
    // Nothing to do - ratings are passed in as a prop
  } else if (props.kind === "TeamEffectivenessAggregateDiamond") {
    getTeamEffectivenessReport(props.headId || undefined).then((data) => {
      altRatings.value = data.aggregateRatings;
      const hasData = data.questionnaires && data.questionnaires.length > 0;
      emitDataLoaded(hasData);
    });
  }
  if (
    props.kind === "TeamEffectivenessAggregateDiamond" ||
    props.kind === "TeamEffectivenessIndividualDiamond"
  ) {
    cms
      .getByUid("cms-team-effectiveness-review", cmsContentMap.teamEffectiveness.questionnaireUid)
      .then((d) => {
        doc.value = d;
      });
  }
}

watch(() => props.kind, updateHtml, { immediate: true });

const html = "";
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="less">
// Styling for this component is contained in src/shared/styles/diamonds.less
.team-diamond-head-shape.team-diamond-head-shape.team-diamond-head-shape {
  background: #fffd;
  &:hover {
    background: #fffd;
  }
  border-width: 4px;
  border-style: solid;
}
.diamond-button-hit-target {
  border-radius: 12px;
  height: 24px;
  width: 24px;
  position: relative;
  top: calc(50% - 12px);
  left: calc(50% - 12px);
}

// .diamond-head-solo {
//   .diamondButton::before {
//     border-radius: 50%;
//   }
// }

// .no-click-diamond-head {
//   user-select: none;
//   cursor: default;
//   pointer-events: none;
// }
</style>
